import { useTranslation } from "react-i18next";

import { useActivityData } from "./useActivityData";
import { WHANAU_STATE_ORDER } from "./whanauState";
import type {
  CohortFilters,
  ReportAggregates,
  WhanauState,
  WhanauStateDistribution,
} from "./reportTypes";
import { pct } from "./format";

/**
 * Whānau States tab (ADR-010, Sue's wishlist #1).
 *
 * Reports on the STATE OF A WHĀNAU as one unit: each whānau is collapsed to a
 * single dominant state (plurality of its factors; ties = mixed; nothing
 * assessed = unassessed), then we show the proportion of whānau in each state —
 * right now, and at entry vs exit, plus the entry→exit transition.
 *
 * "Right now" comes from the activity endpoint (current factor states);
 * entry/exit comes from the aggregates payload already loaded by the page.
 */
type Props = { agg: ReportAggregates; filters: CohortFilters };

const STATE_COLOUR: Record<WhanauState, string> = {
  risk: "var(--state-risk, #c0392b)",
  change: "var(--state-change, #d68910)",
  protective: "var(--state-protective, #2e7d52)",
  mixed: "var(--navy, #1d3557)",
  unassessed: "var(--ink-muted, #9aa3af)",
};

function useStateLabel() {
  const { t } = useTranslation();
  return (s: WhanauState) => t(`reports.whanauState.label.${s}`);
}

/** 100%-stacked bar over the five whānau-states + an on-paper readout. */
function StateDistribution({
  dist,
  caption,
}: {
  dist: WhanauStateDistribution;
  caption: string;
}) {
  const { t } = useTranslation();
  const label = useStateLabel();
  const total = dist.total;
  const summary = WHANAU_STATE_ORDER.filter((s) => dist[s] > 0)
    .map((s) => `${pct(total > 0 ? dist[s] / total : 0)} ${label(s)}`)
    .join(", ");

  return (
    <div className="rpt-wstate">
      <div className="rpt-wstate__head">
        <span className="rpt-wstate__caption">{caption}</span>
        <span className="rpt-wstate__n">
          {t("reports.whanauState.nWhanau", { n: total })}
        </span>
      </div>
      <div
        className="rpt-wstate__bar"
        role="img"
        aria-label={`${caption}: ${summary}`}
        style={{
          display: "flex",
          height: "24px",
          borderRadius: "6px",
          overflow: "hidden",
          border: "1px solid var(--border, #d4d9e0)",
        }}
      >
        {WHANAU_STATE_ORDER.map((s) => {
          const w = total > 0 ? (dist[s] / total) * 100 : 0;
          if (w <= 0) return null;
          return (
            <span
              key={s}
              style={{
                width: `${String(w)}%`,
                background: STATE_COLOUR[s],
                color: "#fff",
                fontSize: "0.72rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {w >= 10 ? pct(dist[s] / total) : ""}
            </span>
          );
        })}
      </div>
      <ul
        className="rpt-readout"
        style={{ listStyle: "none", margin: "8px 0 0", padding: 0 }}
      >
        {WHANAU_STATE_ORDER.map((s) => (
          <li
            key={s}
            className="rpt-readout__item"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              marginRight: "14px",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: "12px",
                height: "12px",
                borderRadius: "3px",
                background: STATE_COLOUR[s],
                display: "inline-block",
              }}
            />
            <span>
              <strong>{pct(total > 0 ? dist[s] / total : 0)}</strong> {label(s)}{" "}
              <span style={{ color: "var(--ink-muted, #5a6472)" }}>
                ({dist[s]})
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Entry→exit transition table: rows = state at entry, columns = state at exit. */
function TransitionTable({ agg }: { agg: ReportAggregates }) {
  const { t } = useTranslation();
  const label = useStateLabel();
  const tr = agg.whanauState.transition;

  return (
    <table
      className="rpt-wstate__matrix"
      style={{ borderCollapse: "collapse" }}
    >
      <caption className="rpt-subhead" style={{ textAlign: "left" }}>
        {t("reports.whanauState.transitionTitle")}
      </caption>
      <thead>
        <tr>
          <th scope="col" style={cellStyle}>
            {t("reports.whanauState.entryToExit")}
          </th>
          {WHANAU_STATE_ORDER.map((s) => (
            <th key={s} scope="col" style={cellStyle}>
              {label(s)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {WHANAU_STATE_ORDER.map((from) => (
          <tr key={from}>
            <th scope="row" style={{ ...cellStyle, textAlign: "left" }}>
              {label(from)}
            </th>
            {WHANAU_STATE_ORDER.map((to) => {
              const n = tr[from][to];
              return (
                <td
                  key={to}
                  style={{
                    ...cellStyle,
                    textAlign: "center",
                    fontWeight: from === to ? 600 : 400,
                    color: n === 0 ? "var(--ink-muted, #9aa3af)" : undefined,
                  }}
                >
                  {n === 0 ? "·" : n}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const cellStyle = {
  border: "1px solid var(--border, #d4d9e0)",
  padding: "6px 10px",
  fontSize: "0.85rem",
} as const;

export function WhanauStatesView({ agg, filters }: Props) {
  const { t } = useTranslation();
  const activity = useActivityData(filters);

  return (
    <div className="rpt-view">
      <section className="rpt-panel">
        <h2 className="rpt-panel__title">
          {t("reports.whanauState.nowTitle")}
        </h2>
        <p className="rpt-panel__lead">{t("reports.whanauState.nowLead")}</p>
        {activity.kind === "ok" ? (
          <StateDistribution
            dist={activity.data.whanauState}
            caption={t("reports.whanauState.rightNow")}
          />
        ) : activity.kind === "loading" ? (
          <p className="rpt__status">{t("reports.loading")}</p>
        ) : activity.kind === "forbidden" ? (
          <p className="rpt__status rpt__status--error">
            {t("reports.forbidden")}
          </p>
        ) : (
          <p className="rpt__status rpt__status--error">
            {t("reports.error", { message: activity.message })}
          </p>
        )}
      </section>

      <section className="rpt-panel">
        <h2 className="rpt-panel__title">
          {t("reports.whanauState.entryExitTitle")}
        </h2>
        <p className="rpt-panel__lead">
          {t("reports.whanauState.entryExitLead")}
        </p>
        <StateDistribution
          dist={agg.whanauState.entry}
          caption={t("reports.atEntry")}
        />
        <div style={{ height: "14px" }} />
        <StateDistribution
          dist={agg.whanauState.exit}
          caption={t("reports.atExit")}
        />
      </section>

      <section className="rpt-panel">
        <p className="rpt-panel__lead">
          {t("reports.whanauState.transitionLead")}
        </p>
        <TransitionTable agg={agg} />
      </section>
    </div>
  );
}

export default WhanauStatesView;
